/**
 * Acessibilidade: escala de UI (Small/Default/Large) e Reduced Motion. Persistido.
 * A escala do usuário MULTIPLICA a escala responsiva automática (ViewportManager).
 */
import { GameConfig } from "../config/GameConfig";

export type UiScaleKey = "small" | "default" | "large";

/** 10.5Y — lado do joystick. "right" = direcional na direita e botões na esquerda (default). */
export type StickSide = "left" | "right";

export interface AccessibilitySettings {
  uiScale: UiScaleKey;
  reducedMotion: boolean;
  stickSide?: StickSide;
  /** 10.6C — distância da câmera (multiplica o offset base). 1 = enquadramento antigo. */
  camZoom?: number;
}

/** 10.6C — níveis de distância da câmera oferecidos nas Configurações. */
export const CAM_ZOOMS = [0.9, 1.05, 1.25, 1.5] as const;
export const CAM_ZOOM_DEFAULT = 1.25;
export function clampCamZoom(v: number): number {
  return Number.isFinite(v) ? Math.min(1.6, Math.max(0.85, v)) : CAM_ZOOM_DEFAULT;
}

export class Accessibility {
  uiScale: UiScaleKey = "default";
  reducedMotion = false;
  /** 10.5Y — direcional na DIREITA por padrão (maioria destra usa o polegar direito p/ mirar/andar). */
  stickSide: StickSide = "right";
  /** 10.6C — o enquadramento padrão era colado no boneco: agora abre 25% e é ajustável. */
  camZoom: number = CAM_ZOOM_DEFAULT;

  /** Aplica o layout dos controles no documento (CSS espelha a partir daqui). */
  applyStickSide(): void {
    document.documentElement.setAttribute("data-stick", this.stickSide);
  }

  get uiScaleMult(): number {
    return GameConfig.accessibility.uiScales[this.uiScale];
  }

  snapshot(): AccessibilitySettings {
    return { uiScale: this.uiScale, reducedMotion: this.reducedMotion, stickSide: this.stickSide, camZoom: this.camZoom };
  }

  load(data: Partial<AccessibilitySettings> | undefined): void {
    const k = data?.uiScale;
    this.uiScale = k === "small" || k === "default" || k === "large" ? k : "default";
    this.reducedMotion = data?.reducedMotion === true;
    this.stickSide = data?.stickSide === "left" ? "left" : "right";
    this.camZoom = clampCamZoom(data?.camZoom ?? CAM_ZOOM_DEFAULT);
    this.applyStickSide();
  }

  reset(): void {
    this.uiScale = "default";
    this.reducedMotion = false;
    this.stickSide = "right";
    this.camZoom = CAM_ZOOM_DEFAULT;
    this.applyStickSide();
  }
}
